// El panel a pantalla completa de MÓVIL, que comparten los campos de estación y el de Ocio.
//
// Va en su propio fichero y no en buscador.js porque ocio.js también lo necesita, y buscador.js
// ya importa ocio.js: dejarlo allí cerraba un ciclo entre los dos módulos.

// Por debajo de 768px, tocar un campo de estación no abre un desplegable: abre un panel que
// ocupa la pantalla (`.contenedor-estacion.open` en el CSS). Mientras está abierto, la página
// de debajo NO se mueve y se sube al principio, que es lo que hace la home; sin eso el panel
// flota sobre una página que sigue desplazándose.
const ES_MOVIL = () => window.matchMedia('(max-width: 768px)').matches

// El apaño de iOS de la home, copiado tal cual: en iPhone/iPad, la PRIMERA vez que se toca un
// campo de destino, el contenedor recibe 45px de relleno arriba --Safari desplaza la página al
// sacar el teclado y el rótulo se quedaba tapado-- y el bloque se marca como `first` para que
// la segunda vez ya no se aplique. En cualquier otro navegador no pasa nada de esto.
const ES_IOS = () => ['iPad Simulator', 'iPhone Simulator', 'iPod Simulator', 'iPad', 'iPhone', 'iPod']
  .includes(navigator.platform) || (navigator.userAgent.includes('Mac') && 'ontouchend' in document)

// Dónde queremos el campo cuando no hay panel: pegado arriba, pero no tanto que se meta debajo
// de la cabecera fija que casi todas las landings llevan.
const ALTO_CABECERA = 90

/**
 * Se traga el `click` que remata el toque que acaba de abrir el panel, y sólo ése.
 *
 * Se pone en CAPTURA para llegar antes que nadie, y se retira solo: con el propio clic, o al
 * empezar el gesto siguiente si ese clic no llega a producirse --un toque que acaba en arrastre
 * no lo genera, y dejarlo puesto se comería el clic bueno del cliente--.
 */
function descartarElClicDeEsteGesto () {
  const tragar = (e) => {
    e.stopPropagation()
    e.preventDefault()
    document.removeEventListener('pointerdown', soltar, true)
  }
  const soltar = () => {
    document.removeEventListener('click', tragar, true)
    document.removeEventListener('pointerdown', soltar, true)
  }
  document.addEventListener('click', tragar, { capture: true, once: true })
  document.addEventListener('pointerdown', soltar, { capture: true, once: true })
}

/** El contenedor que en la home recibe la clase `first`: el del campo, sea estación u ocio. */
function contenedorDelCampo (campo) {
  return campo.closest('.rvb-contenedor-estacion, .rvb-bloque-buscador-actividades')
}

/**
 * ¿Puede el panel ocupar de verdad la pantalla, o la landing se lo impide?
 *
 * El panel es `position: fixed`, y con eso basta en la portada. En una landing no siempre: un
 * `filter`, un `backdrop-filter`, una transformación o una perspectiva convierten al ancestro
 * en contenedor de bloque HASTA PARA UN FIJO, y entonces el 100% × 100% del panel deja de ser
 * la pantalla y pasa a ser esa caja. Es el mismo mecanismo que obliga a sacar el calendario
 * fuera (está contado en portal.js).
 *
 * Medido en corporatebenefits.trenmashotel.com, cuya tarjeta del buscador lleva
 * `backdrop-filter: blur(8px)`: el panel salía de 358×335 y en el sitio donde ya estaba el
 * buscador, con el campo a media pantalla y las estaciones debajo del teclado. Eso es lo que
 * veía el cliente: escribir la ciudad y que no apareciese nada.
 *
 * Y NO SE ARREGLA RECOLOCÁNDOLO. Se probó --medir dónde ha caído y restarle la diferencia-- y
 * el panel vuelve a cubrir la pantalla, pero sigue DENTRO de la baraja de esa tarjeta: la
 * cabecera de la landing, que está fuera y con más z-index, se pinta encima del campo y de la
 * flecha de volver. Salir de ahí es salir del DOM, y por ahí no se puede pasar: dentro del
 * panel va el campo con el foco puesto, y mover un input del árbol le quita el foco y cierra
 * el teclado justo cuando el cliente acaba de tocarlo.
 *
 * Así que cuando pasa, no hay panel: se deja el campo donde está --con la lista de
 * sugerencias, que sí vive fuera de todo esto, colgando debajo-- y se sube la página para que
 * el campo quede arriba y quepan las estaciones entre él y el teclado.
 */
function laPantallaEsSuya (panel) {
  if (!panel) return false
  // Se pregunta por la propiedad Y por su versión con prefijo. `backdrop-filter` sin prefijo no
  // existió en Safari hasta la 18, y es el navegador de la mitad de los móviles: preguntando
  // sólo por la estándar, en un iPhone con Safari 17 el desenfoque está puesto --con
  // `-webkit-backdrop-filter`--, atrapa al fijo igual, y nosotros creíamos que la pantalla era
  // nuestra. Lo mismo vale para `filter`, `transform` y `perspective`, que también se sirven
  // con prefijo desde hojas antiguas.
  const puesto = (cs, propiedad) => {
    const valor = cs.getPropertyValue(propiedad) || cs.getPropertyValue('-webkit-' + propiedad)
    return valor && valor !== 'none'
  }
  // Hasta el <html> incluido: un `transform` en el <body> --que alguna landing usa para su menú
  // lateral-- atrapa al fijo exactamente igual que uno a media altura.
  for (let n = panel.parentElement; n; n = n.parentElement) {
    const cs = getComputedStyle(n)
    if (puesto(cs, 'filter') || puesto(cs, 'backdrop-filter')) return false
    if (puesto(cs, 'transform') || puesto(cs, 'perspective')) return false
    if (/transform|filter|perspective/.test(cs.willChange)) return false
    if (/paint|strict|content/.test(cs.contain)) return false
  }
  return true
}

/**
 * Sin panel: el campo, arriba del todo, y a esperar la lista.
 *
 * Ni se bloquea el scroll de la página ni se sube al principio, que son las dos cosas que hace
 * el panel: sin panel que tape, subir al principio deja al cliente mirando la cabecera de la
 * landing con el teclado fuera y el campo donde estaba. Lo que se hace es lo contrario, llevar
 * el campo a la primera línea, que es lo que despeja el hueco donde va la lista.
 */
function subirElCampo (campo) {
  const r = campo.getBoundingClientRect()
  const cuanto = r.top - ALTO_CABECERA
  if (Math.abs(cuanto) < 4) return
  // `scrollBy` y no `scrollIntoView`: el segundo elige por su cuenta el ancestro que desplaza y
  // en una landing con contenedores propios acaba moviendo una caja interior en vez de la
  // página. Sin animación: con el teclado saliendo a la vez, el desplazamiento suave se queda a
  // medias.
  window.scrollBy({ top: cuanto, left: 0, behavior: 'auto' })
}

export function abrirPanelMovil (campo) {
  if (!ES_MOVIL()) return
  const panel = campo.closest('.rvb-contenedor-estacion')

  if (panel && !laPantallaEsSuya(panel)) {
    // La clase la acaba de poner quien nos llama, y es la que enciende el panel en el CSS.
    // Aquí se retira: sin ella el campo se queda como está y la lista cuelga debajo, que es lo
    // que sí funciona en una landing con la tarjeta filtrada.
    panel.classList.remove('rvb-open')
    // Aquí no hay panel, pero el campo se sube: la página se mueve igual bajo el dedo, así que
    // el clic que remata el toque cae donde no debe --medido en la home de Corporate Benefits:
    // en el hero--. Ahí no pasa nada porque no es pulsable, pero en otra página podría serlo.
    descartarElClicDeEsteGesto()
    subirElCampo(campo)
    return
  }

  // EL CLIC QUE SOBRA DEL MISMO TOQUE, QUE ABRÍA Y CERRABA EL PANEL DE GOLPE.
  //
  // Un toque en el campo es `pointerdown` -> `mousedown` -> foco -> `click`. El panel lo abre el
  // FOCO, así que cuando llega el `click` la pantalla ya es otra: donde estaba el dedo hay ahora
  // la flecha de volver del panel. Ese clic la pulsa, el campo pierde el foco y el panel se
  // cierra en el mismo gesto que lo abrió. Desde fuera se ve como que "el primer toque no hace
  // nada" y hay que tocar otra vez.
  //
  // No es de iOS: se reproduce en el emulador. Pero hasta ahora no se veía en las landings
  // porque allí la tarjeta lleva `backdrop-filter` y el panel NO llega a abrirse (ver
  // `laPantallaEsSuya`); dentro del modal del buscador, que no lo lleva, sí se abre.
  descartarElClicDeEsteGesto()
  document.body.classList.add('rvb-modal-calendar-open')
  // La marca de que el panel está abierto DE VERDAD. De ella cuelga en el CSS que la píldora de
  // la maqueta apilada deje de recortar: `overflow: hidden` es lo que le da sus esquinas
  // redondeadas, y con el panel dentro le cortaba todo menos los 58px de alto del recuadro.
  const bloque = campo.closest('.rvb-bloque-estaciones')
  if (bloque) bloque.classList.add('rvb-panel-abierto')
  window.scrollTo(0, 0)
  if (!ES_IOS() || !campo.classList.contains('rvb-station2Input')) return
  const marca = contenedorDelCampo(campo)
  if (!marca) return
  // La caja que lleva el relleno es la hija del marcado por la que cuelga el campo: el
  // <label> en las estaciones, el contenedor del buscador en Ocio.
  let caja = campo.parentElement
  while (caja && caja.parentElement !== marca) caja = caja.parentElement
  if (!caja) return
  if (!marca.classList.contains('rvb-first')) {
    marca.classList.add('rvb-first')
    caja.style.paddingTop = '45px'
  } else {
    caja.style.paddingTop = '0px'
  }
}

export function cerrarPanelMovil (campo) {
  document.body.classList.remove('rvb-modal-calendar-open')
  const bloque = campo.closest('.rvb-bloque-estaciones')
  if (bloque) bloque.classList.remove('rvb-panel-abierto')
  const marca = contenedorDelCampo(campo)
  if (!marca) return
  let caja = campo.parentElement
  while (caja && caja.parentElement !== marca) caja = caja.parentElement
  if (caja) caja.style.paddingTop = ''
}
